/**
 * F2.e - Progression event listener.
 *
 * Mounted once at the app root. Subscribes to `dosti:progression_delta`
 * window events (emitted by the axios response interceptor when a
 * response carries `_progression_delta`) and dispatches:
 *   - Sonner toast on level-up
 *   - shadcn Dialog for rank-up (bigger moment)
 *   - Sonner toast per achievement unlock, PLUS a queued modal when
 *     multiple unlocks arrive together
 *
 * Design constraints (spec section 19):
 *   - No confetti, no coins, no arcade sound
 *   - Subtle motion (fade + slight scale), respects `prefers-reduced-motion`
 *   - Emit accessible copy - toasts are role=status, modal has proper title
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, ChevronRight } from 'lucide-react';

const RARITY_TEXT = {
  Common: 'text-neutral-600',
  Uncommon: 'text-success-700',
  Rare: 'text-blue-700',
  Epic: 'text-purple-700',
  Legendary: 'text-secondary-800',
  Legacy: 'text-amber-800',
};

const ProgressionEventListener = () => {
  const navigate = useNavigate();
  const [rankUp, setRankUp] = useState(null); // {old_rank_display, new_rank_display}
  const [unlockQueue, setUnlockQueue] = useState([]); // list of {code,name,rarity,bonus_points}
  const [unlockIdx, setUnlockIdx] = useState(0);

  const dismissRankUp = () => setRankUp(null);
  const nextUnlock = () => {
    if (unlockIdx + 1 >= unlockQueue.length) {
      setUnlockQueue([]);
      setUnlockIdx(0);
    } else {
      setUnlockIdx(unlockIdx + 1);
    }
  };

  const goToAchievements = () => {
    setUnlockQueue([]);
    setUnlockIdx(0);
    navigate('/profile?tab=achievements');
    // ProfilePage reads ?tab= param to select the tab. If it doesn't
    // yet, we still deep-link the profile page; user finds Achievements.
  };

  const handleDelta = useCallback((delta) => {
    if (!delta) return;

    if (delta.leveled_up && !delta.rank_changed) {
      toast.success(
        `Level up. Now Level ${delta.new_level}.`,
        {
          duration: 4000,
          className: 'border-l-4 border-l-ember-600',
          // Sonner injects into a shared toaster - we add a testid via id.
          id: 'levelup-toast',
        },
      );
    }

    if (delta.rank_changed) {
      // Rank-up is a bigger moment - modal instead of toast.
      setRankUp({
        old_rank_display: delta.old_rank_display,
        new_rank_display: delta.new_rank_display,
        new_level: delta.new_level,
      });
    }

    const unlocks = Array.isArray(delta.unlocked_achievements)
      ? delta.unlocked_achievements : [];
    if (unlocks.length === 1) {
      const u = unlocks[0];
      toast.success(
        `Unlocked · ${u.name}`,
        {
          description: `${u.rarity} · +${u.bonus_points ?? 0} points`,
          duration: 5000,
          id: `achievement-unlock-toast-${u.code}`,
          className: 'border-l-4 border-l-secondary-500',
        },
      );
    } else if (unlocks.length >= 2) {
      // Queue reveal modal.
      setUnlockQueue(unlocks);
      setUnlockIdx(0);
    }
  }, []);

  useEffect(() => {
    const listener = (e) => handleDelta(e.detail);
    window.addEventListener('dosti:progression_delta', listener);
    return () => window.removeEventListener('dosti:progression_delta', listener);
  }, [handleDelta]);

  const currentUnlock = unlockQueue[unlockIdx];

  return (
    <>
      {/* Rank-up modal (bigger moment) */}
      <Dialog open={!!rankUp} onOpenChange={(v) => !v && dismissRankUp()}>
        <DialogContent
          data-testid="rankup-modal"
          className="max-w-md overflow-hidden motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95"
        >
          {/* Subtle path/compass background pattern - CSS radial only, no cartoon assets */}
          <div className="absolute inset-0 -z-10 pointer-events-none opacity-30"
            style={{
              backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(155,79,45,0.15), transparent 40%), radial-gradient(circle at 80% 80%, rgba(30,58,95,0.12), transparent 40%)',
            }}
          />
          <DialogHeader>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-secondary-600 mb-1">
              <Sparkles className="h-3 w-3" /> A new circle
            </div>
            <DialogTitle className="font-display text-2xl text-primary-900">
              You have crossed into a new rank.
            </DialogTitle>
          </DialogHeader>
          {rankUp && (
            <div className="space-y-4 py-3">
              <div className="flex items-center gap-3">
                <div className="text-sm text-primary-600">
                  <div className="text-[10px] uppercase tracking-widest text-primary-500">From</div>
                  <div className="font-display font-semibold">{rankUp.old_rank_display}</div>
                </div>
                <ArrowRight className="h-5 w-5 text-secondary-600 mx-2" />
                <div className="text-sm">
                  <div className="text-[10px] uppercase tracking-widest text-primary-500">Now</div>
                  <div className="font-display font-bold text-ember-800 text-lg">{rankUp.new_rank_display}</div>
                </div>
              </div>
              <p className="text-sm text-primary-700 italic">
                Level {rankUp.new_level} unlocks a new stretch of your Dosti journey. No leaderboards, just yours.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={dismissRankUp}
              data-testid="rankup-modal-close"
              className="bg-ember-700 hover:bg-ember-800 text-white"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Achievement unlock reveal (queue when 2+ unlocks arrive together) */}
      <Dialog open={unlockQueue.length > 0} onOpenChange={(v) => !v && setUnlockQueue([])}>
        <DialogContent
          data-testid="achievement-unlock-modal"
          className="max-w-md motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95"
        >
          <DialogHeader>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-secondary-600 mb-1">
              <Sparkles className="h-3 w-3" />
              {unlockQueue.length > 1 && (
                <span>{unlockIdx + 1} of {unlockQueue.length}</span>
              )}
            </div>
            <DialogTitle className="font-display text-2xl text-primary-900">
              {currentUnlock?.name}
            </DialogTitle>
          </DialogHeader>
          {currentUnlock && (
            <div className="space-y-3 py-2">
              <div className={`text-xs uppercase tracking-widest ${RARITY_TEXT[currentUnlock.rarity] || 'text-neutral-600'}`}>
                {currentUnlock.rarity} · +{currentUnlock.bonus_points ?? 0} points
              </div>
              <p className="text-sm text-primary-700">
                A new achievement has landed in your collection.
              </p>
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button
              onClick={goToAchievements}
              data-testid="achievement-unlock-modal-view-profile"
              variant="outline"
            >
              View in profile
            </Button>
            {unlockIdx + 1 < unlockQueue.length ? (
              <Button
                onClick={nextUnlock}
                data-testid="achievement-unlock-modal-next"
                className="bg-ember-700 hover:bg-ember-800 text-white"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={nextUnlock}
                data-testid="achievement-unlock-modal-next"
                className="bg-ember-700 hover:bg-ember-800 text-white"
              >
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProgressionEventListener;
